import { useEffect } from 'react'

/**
 * Navigation à la télécommande pour TOUTE l'app en mode TV.
 *
 * Flèches : le focus va vers le bouton le plus proche dans la direction
 * appuyée (calculé uniquement à l'appui, jamais en continu).
 * OK / Entrée : active le bouton (comportement natif des <button>).
 *
 * Une page peut gérer elle-même une touche : il lui suffit d'appeler
 * event.preventDefault(), ce hook l'ignorera.
 */

const FOCUSABLE = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

const DIRS = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' }

const isVisible = (el) => {
  const r = el.getBoundingClientRect()
  if (r.width < 2 || r.height < 2) return false
  const st = window.getComputedStyle(el)
  return st.visibility !== 'hidden' && st.display !== 'none'
}

const gap = (a1, a2, b1, b2) => (a2 < b1 ? b1 - a2 : b2 < a1 ? a1 - b2 : 0)

function pick(active, items, dir) {
  const a = active.getBoundingClientRect()
  const ax = a.left + a.width / 2
  const ay = a.top + a.height / 2
  const vertical = dir === 'up' || dir === 'down'
  const aligned = []
  const cone = []

  for (const el of items) {
    if (el === active) continue
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    let main
    let cross

    if (dir === 'right') {
      if (cx <= ax + 2) continue
      main = Math.max(0, r.left - a.right)
      cross = gap(a.top, a.bottom, r.top, r.bottom)
    } else if (dir === 'left') {
      if (cx >= ax - 2) continue
      main = Math.max(0, a.left - r.right)
      cross = gap(a.top, a.bottom, r.top, r.bottom)
    } else if (dir === 'down') {
      if (cy <= ay + 2) continue
      main = Math.max(0, r.top - a.bottom)
      cross = gap(a.left, a.right, r.left, r.right)
    } else {
      if (cy >= ay - 2) continue
      main = Math.max(0, a.top - r.bottom)
      cross = gap(a.left, a.right, r.left, r.right)
    }

    // Départage : on préfère l'élément aligné sur le même bord
    // (gauche en vertical, haut en horizontal), comme on lit.
    const edge = vertical ? Math.abs(r.left - a.left) : Math.abs(r.top - a.top)
    const entry = { el, score: main + cross * 3 + edge * 0.1 }

    if (cross === 0) aligned.push(entry)
    // Hors alignement : seulement si l'élément est nettement plus loin
    // dans la direction que sur le côté (pas de saut en diagonale)
    else if (main > 0 && cross <= main * 0.6) cone.push(entry)
  }

  // D'abord les éléments en face ; sinon ceux dans la direction
  // (jamais de saut en diagonale lointaine vers une autre colonne).
  const pool = aligned.length ? aligned : cone
  let best = null
  for (const c of pool) if (!best || c.score < best.score) best = c
  return best ? best.el : null
}

const scopeRoot = () =>
  document.querySelector('[aria-modal="true"]:not([aria-hidden="true"])') ||
  document.querySelector('.app-content') ||
  document.body

export function focusInitial() {
  const root = scopeRoot()
  const el = root.querySelector('[data-tv-initial-focus]') ||
    [...root.querySelectorAll(FOCUSABLE)].find(isVisible)
  el?.focus({ preventScroll: true })
}

export function useTVRemote(enabled, pathname) {
  // Focus de départ à chaque changement de page
  useEffect(() => {
    if (!enabled) return undefined
    const t = setTimeout(() => {
      const active = document.activeElement
      if (!active || active === document.body || !document.contains(active)) focusInitial()
    }, 250)
    return () => clearTimeout(t)
  }, [enabled, pathname])

  useEffect(() => {
    if (!enabled) return undefined

    const onKey = (e) => {
      if (e.defaultPrevented) return
      // Page qui gère déjà ses flèches (ex. profil TV) : on la laisse faire
      if (document.querySelector('[data-tv-own-nav]')) return
      const dir = DIRS[e.key]
      if (!dir) return

      const active = document.activeElement
      const tag = active?.tagName
      const type = (active?.type || '').toLowerCase()
      const typing = tag === 'TEXTAREA' ||
        (tag === 'INPUT' && !['range', 'checkbox', 'radio', 'button', 'submit'].includes(type))
      // Dans un champ texte ou un curseur, gauche/droite gardent leur rôle
      if ((typing || type === 'range') && (dir === 'left' || dir === 'right')) return

      const root = scopeRoot()
      const items = [...root.querySelectorAll(FOCUSABLE)].filter(isVisible)
      if (!items.length) return
      e.preventDefault()

      if (!items.includes(active)) { focusInitial(); return }

      const next = pick(active, items, dir)
      if (!next) return
      next.focus({ preventScroll: true })

      // Défilement instantané uniquement si l'élément sort de l'écran
      const r = next.getBoundingClientRect()
      if (r.top < 0 || r.bottom > window.innerHeight || r.left < 0 || r.right > window.innerWidth) {
        next.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
}
